// ============================================================================
// RencontresPage — liste des rencontres CG (E3b, F-430..F-435) — /rencontres
// ============================================================================
// Portage PoC (d)/rencontres/page.tsx :
//   - garde SERVEUR management (M-402) -> 403 = ecran refus ;
//   - filtre A venir / Toutes (?status=all, M-411) ;
//   - cartes drill-in + badge statut ; EmptyState honnete (tables vides) ;
//   - Nouveau = ADMIN (viewer.canCreate).

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  PageHeader, EmptyState, StatusBadge, SkeletonCard, QueryError,
  GsSegmented, GS_COMPONENTS,
} from '@gs/ui';
import { Plus, ShieldAlert, CalendarDays } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useMeetings, type MeetingSummary, type MeetingStatus } from '../../hooks/useMeetings';
import { NewMeetingDialog } from '../../components/rencontres/NewMeetingDialog';
import { formatZonedDateTime } from '../../lib/time/zone';

const STATUS_BADGE: Record<MeetingStatus, 'info' | 'success' | 'neutral'> = {
  upcoming: 'info',
  completed: 'success',
  cancelled: 'neutral',
};

export function MeetingStatusBadge({ status }: { status: MeetingStatus }) {
  const { t } = useTranslation();
  return (
    <StatusBadge variant={STATUS_BADGE[status]}>
      {t(`gest:rencontres.status.${status}`)}
    </StatusBadge>
  );
}

export function MeetingsForbidden() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<ShieldAlert size={48} aria-hidden="true" />}
      title={t('gest:rencontres.forbidden.title')}
      description={t('gest:rencontres.forbidden.description')}
    />
  );
}

function MeetingCard({ meeting }: { meeting: MeetingSummary }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/rencontres/${meeting.id}`}
      className={`${GS_COMPONENTS.card} flex touch-manipulation flex-col gap-3 p-5 transition-colors hover:border-slate-300 dark:hover:border-slate-500`}
    >
      <header className="flex items-start justify-between gap-3">
        <h3 className="min-w-0 truncate text-field font-semibold text-slate-900 dark:text-slate-100">
          {meeting.title}
        </h3>
        <MeetingStatusBadge status={meeting.status} />
      </header>
      <p className="text-field text-slate-500 dark:text-slate-400">
        {formatZonedDateTime(meeting.scheduledAt)}
      </p>
      {meeting.location ? (
        <p className="line-clamp-1 text-meta text-slate-500 dark:text-slate-400">
          {meeting.location}
        </p>
      ) : null}
      <div className="mt-auto text-meta text-slate-500 dark:text-slate-400">
        {t('gest:rencontres.card.documents', { count: meeting.documentCount })}
      </div>
    </Link>
  );
}

export default function RencontresPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);

  const showAll = searchParams.get('status') === 'all';
  const errorCode = searchParams.get('error');

  const listQ = useMeetings(showAll, !!user);

  function setError(code: string) {
    const next = new URLSearchParams(searchParams);
    next.set('error', code);
    setSearchParams(next);
  }

  function setStatusFilter(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.set('status', 'all');
    else next.delete('status');
    next.delete('error');
    setSearchParams(next);
  }

  const forbidden = listQ.isError && listQ.error instanceof ApiForbiddenError;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:rencontres.title')}
        description={t('gest:rencontres.description')}
        onRefresh={() => void listQ.refetch()}
        refreshLoading={listQ.isFetching}
        actions={
          <GsSegmented
            ariaLabel={t('gest:rencontres.filter.label')}
            value={showAll ? 'all' : 'upcoming'}
            onChange={setStatusFilter}
            options={[
              { value: 'upcoming', label: t('gest:rencontres.filter.upcoming') },
              { value: 'all', label: t('gest:rencontres.filter.all') },
            ]}
          />
        }
      />

      {errorCode ? (
        <div
          role="alert"
          className="rounded-xl border border-slate-200 border-l-3 border-l-red-500 bg-white px-4 py-3 text-field text-red-700 dark:border-slate-700 dark:border-l-red-500 dark:bg-slate-800 dark:text-red-300"
        >
          {t(`gest:rencontres.errors.${['invalid', 'forbidden', 'notfound', 'server'].includes(errorCode) ? errorCode : 'server'}`)}
        </div>
      ) : null}

      {forbidden ? (
        <MeetingsForbidden />
      ) : listQ.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : listQ.isError ? (
        <QueryError message={t('gest:rencontres.loadError')} onRetry={() => void listQ.refetch()} />
      ) : listQ.data ? (
        <>
          {listQ.data.viewer.canCreate ? (
            <div>
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5`}
              >
                <Plus size={16} aria-hidden="true" />
                {t('gest:rencontres.new')}
              </button>
            </div>
          ) : null}

          {listQ.data.meetings.length === 0 ? (
            <EmptyState
              icon={<CalendarDays size={48} aria-hidden="true" />}
              title={showAll ? t('gest:rencontres.emptyAll') : t('gest:rencontres.emptyUpcoming')}
              description={t('gest:rencontres.emptyHint')}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {listQ.data.meetings.map((m) => (
                <MeetingCard key={m.id} meeting={m} />
              ))}
            </div>
          )}
        </>
      ) : null}

      <NewMeetingDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onError={setError} />
    </div>
  );
}
